// src/hooks/useChatActions.js

import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';

import API_CONFIG from '../config/api';
import useStreamingChat from './useStreamingChat';
import useDualStreaming from './useDualStreaming';
import useWebSocket from './useWebSocket';

const useChatActions = (
  currentChat, 
  templates, 
  onChatUpdate, 
  onSidebarToggle,
  // Состояния, поднятые в родительский компонент
  message,
  setMessage,
  attachedFiles,
  setAttachedFiles
) => {
  const location = useLocation();
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [isDualComparisonMode, setIsDualComparisonMode] = useState(false);
  const [selectedModelsForComparison, setSelectedModelsForComparison] = useState([]);

  // ВОЗВРАЩАЕМ ЛОКАЛЬНОЕ СОСТОЯНИЕ ДЛЯ ФАЙЛОВ ТЕКУЩЕЙ СЕССИИ
  const [processingFiles, setProcessingFiles] = useState([]); 

  const fileUploadRef = useRef(null);

  const streamingChat = useStreamingChat();
  const dualStreaming = useDualStreaming();
  
  // WebSocket для получения статуса обработки файлов
  const { jobs, clearJobs, socketRef } = useWebSocket(localStorage.getItem('token'));
  
  const autoHideSidebar = localStorage.getItem('autoHideSidebar') !== 'false';

  // Функция для обновления чата с сервера
  const refreshChat = async (chatId) => {
    try {
      const response = await axios.get(`/api/chat/${chatId}`);
      if (response.data) {
        onChatUpdate(response.data);
        // console.log('🔄 Чат обновлен после завершения обработки файла');
      }
    } catch (error) {
      console.error('❌ Ошибка обновления чата:', error);
    }
  };

  // Эффект для автоматического выбора шаблона из URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const templateIdFromUrl = queryParams.get('template');
    if (templateIdFromUrl && templates.some(t => t._id === templateIdFromUrl)) {
      setSelectedTemplateId(templateIdFromUrl);
      // Показываем подсказку пользователю
      toast.success('Шаблон автоматически выбран для использования');
    }
  }, [location.search, templates]);

  // УЛУЧШЕННЫЙ Эффект для обновления статуса обрабатываемых файлов через WebSocket
  useEffect(() => {
    // Отключаем избыточное логирование
    // console.log('🔄 [USE EFFECT] processingFiles changed:', processingFiles.length, processingFiles.map(f => ({ name: f.name, status: f.status })));
    if (processingFiles.length === 0) {
        return; 
    }

    setProcessingFiles(currentFiles => {
      const assignedJobIds = new Set(currentFiles.map(f => f.jobId).filter(Boolean));
      
      const sortedJobs = [...jobs].sort((a, b) => {
        try {
          const timeA = parseInt(a.jobId.split('-')[1] || '0', 10);
          const timeB = parseInt(b.jobId.split('-')[1] || '0', 10);
          return timeB - timeA;
        } catch (e) {
          return 0;
        }
      });

      const newFiles = currentFiles.map(file => {
        let matchingJob;

        if (file.jobId) {
          // Поиск по ID, если он уже есть
          matchingJob = sortedJobs.find(j => j.jobId === file.jobId);
        } else {
          // УСОВЕРШЕНСТВОВАННЫЙ ПОИСК: по имени И по времени создания
          matchingJob = sortedJobs.find(j => {
            if (j.fileName !== file.name || assignedJobIds.has(j.jobId)) {
              return false;
            }
            // Сверяем время создания задачи с временем создания "оптимистичного" файла
            const jobTimestamp = parseInt(j.jobId.split('-')[1] || '0', 10);
            // Допускаем расхождение в 20 секунд
            return Math.abs(jobTimestamp - file.timestamp) < 20000;
          });
        }

        if (!matchingJob) {
          return file;
        }
        
        const prog = matchingJob.progress;
        const percent = (typeof prog === 'object' && prog !== null) ? prog.percent : (typeof prog === 'number' ? prog : 0);
        const message = (typeof prog === 'object' && prog !== null) ? prog.message : undefined;

          return {
            ...file,
          jobId: file.jobId || matchingJob.jobId,
            status: matchingJob.status,
          progress: percent,
          stage: matchingJob.stage || message || file.stage,
          error: matchingJob.error || file.error
        };
      });

      // ОТСЛЕЖИВАЕМ ЗАВЕРШЕНИЕ ЗАДАЧ И ОБНОВЛЯЕМ ЧАТ
      const completedTasks = newFiles.filter(f => f.status === 'completed');
      if (completedTasks.length > 0 && currentChat?._id) {
        // Обновляем чат через небольшую задержку, чтобы бэкенд успел записать данные
        setTimeout(() => refreshChat(currentChat._id), 500);
      }

      // Убираем завершенные файлы через 3 секунды после завершения
      completedTasks.forEach(task => {
        setTimeout(() => {
          setProcessingFiles(prev => prev.filter(f => f.id !== task.id && f.jobId !== task.jobId));
        }, 3000);
      });

      return newFiles;
    });
  }, [jobs, processingFiles.length]);

  // --- Новый эффект: фильтрация задач по текущему чату и восстановление после перезагрузки ---
  useEffect(() => {
    if (!currentChat?._id) return;

    // 1. Убираем задачи, не относящиеся к текущему чату
    setProcessingFiles(prev => prev.filter(f => !f.chatId || f.chatId === currentChat._id));

    // 2. Если нет локальных задач, но на сервере они есть (после перезагрузки страницы)
    if (processingFiles.length === 0 && jobs.length > 0) {
      const restored = jobs
        .filter(j => (j.chatId ? j.chatId === currentChat._id : true))
        .map(j => ({
          id: j.jobId,
          name: j.fileName,
          size: j.fileSize,
          status: j.status,
          progress: j.progress || 0,
          stage: j.stage || 'В очереди',
          jobId: j.jobId,
          chatId: j.chatId
        }));
      if (restored.length > 0) {
        setProcessingFiles(restored);
      }
    }
  }, [currentChat?._id, jobs]);

  const handleUploadError = (errorMessage) => {
    setUploadError(errorMessage);
    toast.error(errorMessage);
    setTimeout(() => setUploadError(''), 5000);
  };
  
  // НОВАЯ асинхронная версия загрузки файлов
  const uploadFilesAsync = async (files, chatId, prompt) => {
    try {
      // console.log('🚀 [ASYNC UPLOAD] Начинаем асинхронную загрузку файлов...');
      
      const formData = new FormData();
      files.forEach(fileData => {
        const file = fileData.file ? fileData.file : fileData;
        formData.append('files', file);
      });
      formData.append('chatId', chatId);
      if (prompt && prompt.trim()) {
        formData.append('prompt', prompt.trim());
      }

      // 1. Optimistic файлы + прогресс загрузки
      const optimisticFiles = files.map((fileData, index) => {
        const file = fileData.file ? fileData.file : fileData;
        const cancelSource = axios.CancelToken.source();
        return {
          id: `${Date.now()}-${index}`,
          name: file.name,
          size: file.size,
          status: 'uploading',
          progress: 0,
          timestamp: Date.now(),
          stage: 'Загрузка...',
          jobId: null,
          chatId,
          cancelSource
        };
      });

      setProcessingFiles(prev => [...prev, ...optimisticFiles]);
      
      // 2. Отправляем через axios и отслеживаем прогресс
      const axiosConfig = API_CONFIG.getAxiosUploadConfig(
        localStorage.getItem('token'),
        (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setProcessingFiles(prev => prev.map(f => {
            const match = optimisticFiles.find(o => o.id === f.id);
            return match ? { ...f, progress: percent } : f;
          }));
        },
        optimisticFiles[0].cancelSource.token
      );

      const { data: uploadResult } = await axios.post(
        `${API_CONFIG.getBaseURL()}/api/files/upload`,
        formData,
        axiosConfig
      );

      // 2. Создаем optimistic файлы для отображения прогресса
      const serverFiles = uploadResult.files || [];

      // 3. Обновляем optimistic файлы на status accepted
      setProcessingFiles(prev => prev.map(f => {
        const match = optimisticFiles.find(o => o.id === f.id);
        if (!match) return f;
        const serverMatch = serverFiles.find(s => s.fileName === f.name);
        if (!serverMatch) return f;
        return {
          ...f,
          status: serverMatch.status || 'queued',
          progress: 0,
          stage: 'В очереди',
          size: serverMatch.fileSize,
          jobId: serverMatch.jobId,
          queue: serverMatch.queue
        };
      }));

      toast.success(`${serverFiles.length} файлов принято в обработку`);
      
      return {
        success: true,
        message: uploadResult.message,
        filesCount: serverFiles.length,
        note: uploadResult.note,
        files: serverFiles  // Добавляем files в return для удобства
      };

    } catch (error) {
      console.error('❌ [ASYNC UPLOAD] Ошибка загрузки:', error);
      throw error;
    }
  };

  // 🚫 Отмена локальной загрузки (до ответа 202)
  const cancelLocalUpload = (optimisticId) => {
    setProcessingFiles(prev => {
      let target = prev.find(f => f.id === optimisticId && f.status === 'uploading');
      if (target && target.cancelSource) {
        try { target.cancelSource.cancel('User cancelled upload'); } catch {}
      }
      return prev.filter(f => f.id !== optimisticId);
    });
  };

  const sendMessage = async (content, selectedTemplateId, apiModelId) => {
    if (!currentChat || (!content.trim() && attachedFiles.length === 0)) return;

    // --- ШАГ 0: Предварительная подготовка (остается без изменений) ---
    if (dualStreaming.hasPendingChoice) {
      const result = await dualStreaming.autoChooseFirstOption(currentChat._id);
      if (result?.chat) onChatUpdate(result.chat);
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    if (autoHideSidebar) {
      setTimeout(() => onSidebarToggle(false), 300);
    }
    streamingChat.resetStream();
    setUploadError('');

    const originalAttachedFiles = [...attachedFiles];
    const hasFiles = originalAttachedFiles.length > 0;
    const hasPrompt = content.trim();

    // Очищаем прикрепленные файлы в любом случае
    setAttachedFiles([]);
    if (fileUploadRef.current) fileUploadRef.current.clearAttachedFiles();

    // ИСПРАВЛЕННАЯ ЛОГИКА: если есть файлы, сначала загружаем их асинхронно
    if (hasFiles) {
      try {
        setLoading(true);
        if (!apiModelId) throw new Error('API модель не выбрана');
        
        // Очищаем поле ввода
        setMessage('');

        // 1. Асинхронно загружаем файлы через существующую систему
        const uploadResult = await uploadFilesAsync(originalAttachedFiles, currentChat._id, content.trim());
        // console.log('📤 [SEND MESSAGE] Файлы загружены, начинаем ожидание:', uploadResult);
        
        // 1.5. Извлекаем имена файлов для ожидания
        const serverFileNames = uploadResult?.files?.map(f => f.fileName) || 
                                originalAttachedFiles.map(f => f.file ? f.file.name : f.name);
        // console.log('📋 [SEND MESSAGE] Будем ожидать файлы:', serverFileNames);
        
        // 2. Ждем завершения обработки всех файлов через WebSocket  
        // console.log('⏳ [SEND MESSAGE] Ожидаем завершения обработки файлов...');
        // console.log('📋 [SEND MESSAGE] Текущие файлы в обработке:', processingFiles);
        await waitForAllFilesToComplete(serverFileNames);
        // console.log('✅ [SEND MESSAGE] Все файлы обработаны!');
        
        // 3. Обновляем чат чтобы получить результаты обработки файлов
        // console.log('🔄 [SEND MESSAGE] Обновляем чат...');
        await refreshChat(currentChat._id);
        
        // 4. Логика обработки в зависимости от наличия промпта и шаблона
        const hasTemplate = selectedTemplateId && templates.find(t => t._id === selectedTemplateId);
        const fileNames = serverFileNames.join(', ');
        
        if (!hasPrompt && !hasTemplate) {
          // СЛУЧАЙ 1: Только файл без промпта и шаблона - сохраняем в контекст
          // console.log('📄 [SEND MESSAGE] Только файл без промпта/шаблона - сохраняем в контекст');
          
          try {
            // Сохраняем файл как полное сообщение пользователя в БД
            const response = await axios.post(`/api/chat/${currentChat._id}/messages/save-file-only`, {
              fileNames: serverFileNames
            });
            
            if (response.data?.chat) {
              onChatUpdate(response.data.chat);
            }
            
            setLoading(false);
            toast.success(`Файлы добавлены в контекст: ${fileNames}`);
          } catch (error) {
            console.error('Ошибка сохранения файла в контекст:', error);
            setLoading(false);
            toast.error('Ошибка сохранения файла в контекст');
    }

        } else {
          // СЛУЧАИ 2, 3, 4: Запускаем стриминг
          let systemPrompt = '';
          let userMessage = '';
          let chatDisplayMessage = '';

          if (hasTemplate) {
            systemPrompt = hasTemplate.content || '';
          }

          // Формируем отображение в чате
          let chatDisplayLines = [`📎 ${fileNames}`];
          
          if (hasTemplate) {
            chatDisplayLines.push(`🔧 Шаблон: ${hasTemplate.title}`);
          }
          
          if (hasPrompt) {
            chatDisplayLines.push(content.trim());
          }
          
          chatDisplayMessage = chatDisplayLines.join('\n');
          
          // Для AI всегда отправляем только промпт пользователя (файлы добавляются на backend)
    if (hasPrompt) {
            userMessage = content.trim();
          } else {
            // Если нет промпта, отправляем стандартный запрос
            userMessage = 'Проанализируй содержимое загруженных файлов';
          }

          // Запускаем стриминг (сообщение пользователя будет сохранено backend'ом с displayContent)
          await streamingChat.startStreaming(
            currentChat._id,
            userMessage,
            systemPrompt,
            userMessage,
            (finalText, stats, updatedChat) => {
              setLoading(false);
              if (updatedChat) onChatUpdate(updatedChat);
              toast.success('Сообщение получено');
            },
            (error) => {
              setLoading(false);
              handleUploadError('Ошибка при отправке сообщения: ' + error.message);
            },
            apiModelId,
            chatDisplayMessage, // Передаем краткое отображение
            serverFileNames // Передаем конкретные файлы
          );
        }

      } catch (error) {
        setLoading(false);
        handleUploadError(`Ошибка обработки файлов: ${error.message}`);
      }
    } else if (hasPrompt) {
      // СТАРАЯ ЛОГИКА для обычных текстовых сообщений без файлов

      // Оптимистично добавляем сообщение пользователя в чат
      const userMessage = { role: 'user', content: content.trim(), timestamp: Date.now() };
      onChatUpdate(prev => ({ ...prev, messages: [...prev.messages, userMessage] }));

      // Очищаем поле ввода
      setMessage('');

      try {
        setLoading(true);
        if (!apiModelId) throw new Error('API модель не выбрана');
        
        let systemPrompt = '';
        if (selectedTemplateId) {
          const template = templates.find(t => t._id === selectedTemplateId);
          systemPrompt = template?.content || '';
        }

        await streamingChat.startStreaming(
          currentChat._id,
          content.trim(),
          systemPrompt,
          content.trim(),
          (finalText, stats, updatedChat) => {
            setLoading(false);
            if (updatedChat) onChatUpdate(updatedChat);
            toast.success('Сообщение получено');
          },
          (error) => {
            setLoading(false);
            onChatUpdate(prev => ({ ...prev, messages: prev.messages.slice(0, -1) }));
            handleUploadError('Ошибка при отправке сообщения: ' + error.message);
          },
          apiModelId
        );
      } catch (error) {
        setLoading(false);
        onChatUpdate(prev => ({ ...prev, messages: prev.messages.slice(0, -1) }));
        handleUploadError(`Ошибка потоковой передачи: ${error.message}`);
      }
    }
  };
  
  const sendDualStreamingMessage = async (content, selectedTemplateId) => {
    setLoading(true);
    if (autoHideSidebar) {
      setTimeout(() => onSidebarToggle(false), 300);
    }
    setUploadError('');

    const originalAttachedFiles = [...attachedFiles];
    const hasFiles = originalAttachedFiles.length > 0;
    const hasPrompt = content.trim();
    const hasTemplate = selectedTemplateId && templates.find(t => t._id === selectedTemplateId);

    // Очищаем прикрепленные файлы в любом случае
      setAttachedFiles([]);
      if (fileUploadRef.current) fileUploadRef.current.clearAttachedFiles();

    // НОВОЕ: Проверяем, нужен ли dual streaming
    if (hasFiles && !hasPrompt && !hasTemplate) {
      // СЛУЧАЙ: Только файл без промпта и шаблона - сохраняем в контекст (БЕЗ dual streaming)
      // console.log('📄 [DUAL STREAMING] Только файл без промпта/шаблона - сохраняем в контекст');
      
      try {
        // 1. Асинхронно загружаем файлы через существующую систему
        const uploadResult = await uploadFilesAsync(originalAttachedFiles, currentChat._id, '');
        // console.log('📤 [DUAL STREAMING FILE ONLY] Файлы загружены:', uploadResult);
        
        // 1.5. Извлекаем имена файлов для ожидания
        const serverFileNames = uploadResult?.files?.map(f => f.fileName) || 
                                originalAttachedFiles.map(f => f.file ? f.file.name : f.name);
        // console.log('📋 [DUAL STREAMING FILE ONLY] Будем ожидать файлы:', serverFileNames);
        
        // 2. Ждем завершения обработки всех файлов через WebSocket  
        // console.log('⏳ [DUAL STREAMING FILE ONLY] Ожидаем завершения обработки файлов...');
        await waitForAllFilesToComplete(serverFileNames);
        // console.log('✅ [DUAL STREAMING FILE ONLY] Все файлы обработаны!');
        
        // 3. Обновляем чат чтобы получить результаты обработки файлов
        // console.log('🔄 [DUAL STREAMING FILE ONLY] Обновляем чат...');
        await refreshChat(currentChat._id);
        
        // 4. Сохраняем файл в контекст через API
        const response = await axios.post(`/api/chat/${currentChat._id}/messages/save-file-only`, {
          fileNames: serverFileNames
        });
        
        if (response.data?.chat) {
          onChatUpdate(response.data.chat);
        }
        
        const fileNames = serverFileNames.join(', ');
        setLoading(false);
        toast.success(`Файлы добавлены в контекст: ${fileNames}`);
        return;
      } catch (error) {
        setLoading(false);
        toast.error(`Ошибка обработки файла: ${error.message}`);
        return;
      }
    }

    try {
      let systemPrompt = '';
      if (selectedTemplateId) {
        const template = templates.find(t => t._id === selectedTemplateId);
        systemPrompt = template?.content || '';
      }

      // Очищаем поле ввода
      setMessage('');

      if (hasFiles) {
        // ЛОГИКА: асинхронная обработка файлов + dual streaming
        
        // 1. Асинхронно загружаем файлы через существующую систему
        const uploadResult = await uploadFilesAsync(originalAttachedFiles, currentChat._id, content.trim());
        // console.log('📤 [DUAL STREAMING] Файлы загружены:', uploadResult);
        
        // 1.5. Извлекаем имена файлов для ожидания
        const serverFileNames = uploadResult?.files?.map(f => f.fileName) || 
                                originalAttachedFiles.map(f => f.file ? f.file.name : f.name);
        // console.log('📋 [DUAL STREAMING] Будем ожидать файлы:', serverFileNames);
        
        // 2. Ждем завершения обработки всех файлов через WebSocket
        // console.log('⏳ [DUAL STREAMING] Ожидаем завершения обработки файлов...');
        await waitForAllFilesToComplete(serverFileNames);
        
        // 3. Обновляем чат чтобы получить результаты обработки файлов
        await refreshChat(currentChat._id);
        
        // 4. Формируем сообщения для dual streaming
        const fileNames = serverFileNames.join(', ');
        let chatDisplayLines = [`📎 ${fileNames}`];
        
        if (hasTemplate) {
          chatDisplayLines.push(`🔧 Шаблон: ${hasTemplate.title}`);
          systemPrompt = hasTemplate.content || '';
        }
        
        if (hasPrompt) {
          chatDisplayLines.push(content.trim());
        }
        
        const chatDisplayMessage = chatDisplayLines.join('\n');
        
        // Для AI отправляем промпт пользователя + флаг что нужно добавить файлы
        const userMessage = hasPrompt ? content.trim() : 'Проанализируй содержимое загруженных файлов';
        
        await dualStreaming.startDualStreaming(
          currentChat._id, 
          userMessage,
          chatDisplayMessage,
          selectedModelsForComparison, 
          systemPrompt,
          serverFileNames // передаем конкретные имена файлов
        );

        // Добавляем сообщение пользователя в чат
        const userChatMessage = { role: 'user', content: chatDisplayMessage, timestamp: Date.now() };
        // console.log('💬 [DUAL STREAMING] Добавляем сообщение пользователя в UI:', {
        //   content: chatDisplayMessage,
        //   contentLength: chatDisplayMessage.length,
        //   timestamp: userChatMessage.timestamp
        // });
        onChatUpdate(prev => ({ ...prev, messages: [...prev.messages, userChatMessage] }));
      } else {
        // СТАРАЯ ЛОГИКА: обычное dual streaming без файлов
      await dualStreaming.startDualStreaming(
        currentChat._id, 
          content.trim() || 'Помоги мне',
          content.trim() || 'Помоги мне',
        selectedModelsForComparison, 
        systemPrompt
      );

      // Добавляем сообщение пользователя в чат
        const userMessage = { role: 'user', content: content.trim(), timestamp: Date.now() };
      onChatUpdate(prev => ({ ...prev, messages: [...prev.messages, userMessage] }));
      }

      setLoading(false);
      toast.success('Сравнение моделей запущено');
    } catch (error) {
      setLoading(false);
      toast.error(`Ошибка сравнения моделей: ${error.message}`);
    }
  };

  const handleStopStreaming = () => {
    streamingChat.stopStreaming();
    setLoading(false);
  };
  
  const handleStopDualStreaming = async () => {
    const updatedChat = await dualStreaming.stopDualStreaming(currentChat._id);
    if(updatedChat) onChatUpdate(updatedChat);
  };

  // Функция ожидания завершения обработки файлов через WebSocket jobs
  const waitForAllFilesToComplete = (expectedFileNames = []) => {
    return new Promise((resolve, reject) => {
      
      // Если файлов нет, сразу resolve
      if (expectedFileNames.length === 0) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 120; // 60 секунд (120 * 500мс)
      let completedFiles = new Set();
      
      // Подписываемся на события WebSocket для отслеживания завершения
      const completionHandler = (resultData) => {
        //console.log('🎧 [WAIT FILES] Получено событие завершения:', resultData);
        // console.log('🔍 [WAIT FILES] Ожидаемые файлы:', expectedFileNames);
        const fileName = resultData.fileName || resultData.data?.fileName;
        // console.log('📂 [WAIT FILES] Имя файла из события:', fileName);
        
        if (fileName && expectedFileNames.includes(fileName)) {
          completedFiles.add(fileName);
          // console.log('✅ [WAIT FILES] Файл завершен через событие:', fileName);
          // console.log(`📊 [WAIT FILES] Прогресс: ${completedFiles.size}/${expectedFileNames.length}`);
          
          // Если все файлы завершены
          if (completedFiles.size >= expectedFileNames.length) {
            // console.log('✅ [WAIT FILES] Все файлы обработаны через события!', Array.from(completedFiles));
            cleanup();
            resolve();
          }
        } else {
          // console.log('❌ [WAIT FILES] Файл не совпадает с ожидаемыми:', fileName, 'vs', expectedFileNames);
        }
      };

      const failureHandler = (errorData) => {
        // console.log('❌ [WAIT FILES] Получено событие ошибки:', errorData);
        const fileName = errorData.fileName || errorData.data?.fileName;
        if (fileName && expectedFileNames.includes(fileName)) {
          cleanup();
          reject(new Error(`Ошибка обработки файла ${fileName}: ${errorData.error}`));
        }
      };

      // Подписываемся на события через WebSocket
      const socket = socketRef.current;
      if (socket) {
        // console.log('🎧 [WAIT FILES] Подписываемся на WebSocket события...');
        socket.on('job-completed', completionHandler);
        socket.on('file-processed', completionHandler);
        socket.on('job-failed', failureHandler);
        socket.on('file-failed', failureHandler);
        // console.log('✅ [WAIT FILES] Подписка на события завершена');
      } else {
        console.log('❌ [WAIT FILES] socketRef.current is null!');
      }

      const cleanup = () => {
        if (socket) {
          socket.off('job-completed', completionHandler);
          socket.off('file-processed', completionHandler);
          socket.off('job-failed', failureHandler);
          socket.off('file-failed', failureHandler);
        }
        if (checkInterval) clearInterval(checkInterval);
      };
      
      // Резервная проверка через jobs (для уже завершенных файлов)
      const checkInterval = setInterval(() => {
        attempts++;
        
        // Ищем задачи в WebSocket jobs
        const relevantJobs = jobs.filter(job => 
          expectedFileNames.some(fileName => job.fileName === fileName)
        );
        
        const completedJobs = relevantJobs.filter(job => job.status === 'completed');
        const failedJobs = relevantJobs.filter(job => job.status === 'failed');
        
        // Обновляем завершенные файлы
        completedJobs.forEach(job => {
          if (!completedFiles.has(job.fileName)) {
            completedFiles.add(job.fileName);
            // console.log('✅ [WAIT FILES] Файл завершен в WebSocket jobs:', job.fileName);
          }
        });
        
        // console.log(`🔍 [WAIT FILES] Попытка ${attempts}/${maxAttempts}. Завершено: ${completedFiles.size}/${expectedFileNames.length}. Всего задач: ${relevantJobs.length}`);
        
        if (failedJobs.length > 0) {
          cleanup();
          reject(new Error(`Ошибка обработки файлов: ${failedJobs.map(j => j.fileName).join(', ')}`));
          return;
        }
        
        // Если все файлы завершены
        if (completedFiles.size >= expectedFileNames.length) {
          // console.log('✅ [WAIT FILES] Все файлы обработаны через WebSocket!', Array.from(completedFiles));
          cleanup();
          resolve();
          return;
        }
        
        // Таймаут
        if (attempts >= maxAttempts) {
          cleanup();
          reject(new Error('Превышено время ожидания обработки файлов'));
        }
      }, 500); // Проверяем каждые 500мс
    });
  };

  // Возвращаем все состояния и функции
  return {
    uploadError, handleUploadError,
    loading,
    selectedTemplateId, setSelectedTemplateId,
    fileUploadRef,
    sendMessage,
    
    // Для потоковой передачи
    streamingChat,
    dualStreaming,
    handleStopStreaming,
    
    // Для сравнения моделей
    isDualComparisonMode, setIsDualComparisonMode,
    selectedModelsForComparison, setSelectedModelsForComparison,
    sendDualStreamingMessage,
    handleStopDualStreaming,
    
    // Возвращаем состояния для файлов
    uploadFilesAsync,
    processingFiles,
    setProcessingFiles,
    
    // НОВЫЕ функции асинхронной загрузки
    jobs,                    // Прямая передача jobs из WebSocket
    clearJobs,               // Функция для очистки jobs
    cancelLocalUpload,
  };
};

export default useChatActions;